// Development-only hook for loading room data from API for testing

import { useCallback, useEffect } from 'react';
import { useRoomState } from './useRoomState';
import { getRoomDetails, getPlayerDetails } from '../transport/api/publicApi';
import {
  RoomPlayer,
  RoomState,
  SenderRoleType,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useDevSnapshot');

interface DevSnapshotOptions {
  playerId?: string;
  /** Shallow-merged over the fetched room state, e.g. to rewind a closed
   * room back to `status: 'ended'` for game-over screens. */
  stateOverrides?: Partial<RoomState>;
}

export function useDevSnapshot(
  roomCode?: string,
  options?: DevSnapshotOptions
) {
  const { setMyPlayer, setDevRoomState } = useRoomState();

  const loadDevRoom = useCallback(
    async (targetRoomCode: string, targetOptions?: DevSnapshotOptions) => {
      if (!import.meta.env.DEV) {
        logger.warn('useDevSnapshot is only available in development mode');
        return;
      }

      const playerId = targetOptions?.playerId;
      logger.info(
        `Loading dev room ${targetRoomCode}${playerId ? ` with player ${playerId}` : ''}`
      );

      try {
        // Get room data via API
        const roomResponse = await getRoomDetails(targetRoomCode);
        if (!roomResponse.data.room?.roomState) {
          throw new Error('Room data not found or invalid');
        }

        // Inject room state directly using dev method
        const roomState = roomResponse.data.room.roomState;
        // Ensure roomCode is included in the state (needed for media system)
        roomState.roomCode = targetRoomCode;
        Object.assign(roomState, targetOptions?.stateOverrides);
        setDevRoomState(roomState);
        logger.info('Dev room state injected successfully');

        // Set myPlayer - either specified player or auto-select first player
        if (playerId) {
          // First try to find player in room state (has all room-specific data)
          const playerInRoom = roomState.players?.[playerId];
          if (playerInRoom) {
            setMyPlayer(playerInRoom);
            logger.info(
              `Player ${playerId} (${playerInRoom.playerName}) set as myPlayer from room state`
            );
          } else {
            // Fallback: load player from database and merge with room defaults
            try {
              const playerResponse = await getPlayerDetails({ playerId });
              if (playerResponse.data.player) {
                // Convert PlayerDocument to RoomPlayer with defaults for room-specific fields
                const roomPlayer: RoomPlayer = {
                  playerId: playerResponse.data.player._id,
                  playerName: playerResponse.data.player.playerName,
                  playerColor: playerResponse.data.player.playerColor,
                  face: playerResponse.data.player.face,
                  // Room-specific fields with defaults (since this is dev tooling)
                  role: SenderRoleType.PLAYER,
                  isEliminated: false,
                  scoreTurn: 0,
                  scoreGame: 0,
                  teamId: undefined,
                  hasTwitchAuth:
                    !!playerResponse.data.player.authTwitch?.userId,
                };
                setMyPlayer(roomPlayer);
                logger.info(`Player ${playerId} set as myPlayer from database`);
              } else {
                logger.warn(
                  `Player ${playerId} not found in room state or database`
                );
              }
            } catch (playerError) {
              logger.warn(`Failed to load player ${playerId}:`, playerError);
              // Don't fail the whole operation if player loading fails
            }
          }
        } else {
          // Auto-select player from room state
          if (roomState.players && Object.keys(roomState.players).length > 0) {
            const firstPlayerId = Object.keys(roomState.players)[0];
            const firstPlayer = roomState.players[firstPlayerId];
            setMyPlayer(firstPlayer);
            logger.info(
              `Auto-selected player ${firstPlayer.playerId} (${firstPlayer.playerName}) as myPlayer`
            );
          }
        }
      } catch (error) {
        const errorMsg = `Failed to load dev room: ${error instanceof Error ? error.message : 'Unknown error'}`;
        logger.error(errorMsg, error);
      }
    },
    [setMyPlayer, setDevRoomState]
  );

  // Auto-load room if roomCode is provided
  useEffect(() => {
    if (roomCode && import.meta.env.DEV) {
      loadDevRoom(roomCode, options);
    }
  }, [roomCode, options, loadDevRoom]);

  // Return the manual load function for additional flexibility
  return import.meta.env.DEV ? loadDevRoom : null;
}
